import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../../i18n/contentLocales';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import {
  deleteChallenge,
  listChallenges,
  permanentlyDeleteChallenge,
  restoreChallenge,
} from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { Challenge, MultilingualString } from '../../api/types';
import ConfirmDialog from '../../components/ConfirmDialog';
import DataTable, { type DataTableColumn, type DataTableFilter } from '../../components/DataTable';

const CHALLENGE_TYPES = ['photo', 'text', 'qr_scan', 'quiz', 'creative', 'audio', 'video'] as const;

const ChallengeLibraryList: React.FC = () => {
  const { t, i18n } = useTranslation('content');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Challenge | null>(null);
  const [deleting, setDeleting] = useState(false);
  // SOW 8.6.4 (#344) — org_admin archive view + restore / permanent delete
  const isOrgAdmin = user?.role === 'org_admin';
  const [showArchived, setShowArchived] = useState(false);
  const [toDeleteForever, setToDeleteForever] = useState<Challenge | null>(null);

  const orgSlug = user?.organization_slug;
  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(activeLang === 'de' ? 'de-DE' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listChallenges(orgSlug, { limit: 200, include_deleted: showArchived });
      setChallenges(resp.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const stationNames = useCallback(
    (c: Challenge) => (c.usage?.station_titles ?? []).map((st) => titleOf(st)).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLang],
  );
  const rallyeNames = useCallback(
    (c: Challenge) => (c.usage?.rallye_titles ?? []).map((rt) => titleOf(rt)).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLang],
  );

  const handleDelete = async () => {
    if (!orgSlug || !toDelete) return;
    setDeleting(true);
    try {
      await deleteChallenge(orgSlug, toDelete.id);
      setToDelete(null);
      // Refetch instead of locally filtering: when "Show archived" is on, the
      // just-archived item should stay visible with its archived state
      // (#364), matching what a reload with include_deleted=true would show.
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (c: Challenge) => {
    if (!orgSlug) return;
    try {
      await restoreChallenge(orgSlug, c.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const handleDeleteForever = async () => {
    if (!orgSlug || !toDeleteForever) return;
    setDeleting(true);
    try {
      await permanentlyDeleteChallenge(orgSlug, toDeleteForever.id);
      setChallenges((prev) => prev.filter((c) => c.id !== toDeleteForever.id));
      setToDeleteForever(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<Challenge>[]>(
    () => [
      {
        id: 'name',
        header: t('challenge.list.table.name'),
        searchValue: (c) => titleOf(c.title),
        sortValue: (c) => titleOf(c.title),
        render: (c) => (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {titleOf(c.title) || c.id}
            {c.is_deleted && (
              <Chip
                label={t('archive.archivedChip')}
                color="warning"
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        ),
      },
      {
        id: 'station',
        header: t('challenge.list.table.station'),
        hideBelow: 'sm',
        render: (c) => {
          const names = stationNames(c);
          return (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {names.length > 0 ? names.join(', ') : t('challenge.list.table.unused')}
            </Box>
          );
        },
      },
      {
        id: 'rallye',
        header: t('challenge.list.table.rallye'),
        hideBelow: 'md',
        render: (c) => {
          const names = rallyeNames(c);
          return (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {names.length > 0 ? names.join(', ') : t('challenge.list.table.unused')}
            </Box>
          );
        },
      },
      {
        id: 'points',
        header: t('challenge.list.table.points'),
        align: 'right',
        sortValue: (c) =>
          typeof c.scoring_rules?.max_points === 'number' ? c.scoring_rules.max_points : 0,
        render: (c) =>
          typeof c.scoring_rules?.max_points === 'number' ? c.scoring_rules.max_points : 0,
      },
      {
        id: 'type',
        header: t('challenge.list.table.type'),
        sortValue: (c) => c.challenge_type,
        render: (c) => <Chip label={t(`challenge.types.${c.challenge_type}`)} size="small" />,
      },
      {
        id: 'duration',
        header: t('challenge.list.table.duration'),
        align: 'right',
        hideBelow: 'sm',
        sortValue: (c) => c.time_estimate_minutes ?? 0,
        render: (c) => c.time_estimate_minutes ?? 0,
      },
      {
        id: 'author',
        header: t('challenge.list.table.author'),
        hideBelow: 'md',
        sortValue: (c) => c.created_by_display_name ?? '',
        render: (c) => (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {c.created_by_display_name ?? '—'}
          </Box>
        ),
      },
      {
        id: 'date',
        header: t('challenge.list.table.date'),
        hideBelow: 'lg',
        sortValue: (c) => c.created_at,
        render: (c) => (
          <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {fmtDate(c.created_at)}
          </Box>
        ),
      },
      {
        id: 'actions',
        header: t('challenge.list.table.actions'),
        align: 'right',
        actions: true,
        render: (c) =>
          c.is_deleted ? (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <Tooltip title={t('archive.restore')}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`${t('archive.restore')}: ${titleOf(c.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRestore(c);
                  }}
                >
                  <RestoreFromTrashIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('archive.deleteForever')}>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`${t('archive.deleteForever')}: ${titleOf(c.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDeleteForever(c);
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <Tooltip title={t('challenge.list.table.open')}>
                <IconButton
                  size="small"
                  aria-label={`${t('challenge.list.table.open')}: ${titleOf(c.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/challenges/${c.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('actions.delete', { ns: 'common' })}>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`${t('actions.delete', { ns: 'common' })}: ${titleOf(c.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDelete(c);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, activeLang, navigate, stationNames, rallyeNames],
  );

  const filters = useMemo<DataTableFilter<Challenge>[]>(
    () => [
      {
        id: 'type',
        label: t('challenge.list.filter.type'),
        options: CHALLENGE_TYPES.map((ct) => ({ value: ct, label: t(`challenge.types.${ct}`) })),
        match: (c, value) => c.challenge_type === value,
      },
    ],
    [t],
  );

  const deleteStationNames = toDelete ? stationNames(toDelete) : [];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 1 }}
      >
        <Typography variant="h2" component="h1">
          {t('challenge.list.title')}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/challenges/new')}>
          {t('challenge.list.createButton')}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('challenge.list.subtitle')}
      </Typography>

      <DataTable<Challenge>
        rows={challenges}
        columns={columns}
        getRowId={(c) => c.id}
        ariaLabel={t('challenge.list.title')}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage={
          <>
            <Typography variant="body1" color="text.secondary">
              {t('challenge.list.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('challenge.list.emptyHint')}
            </Typography>
          </>
        }
        searchPlaceholder={t('challenge.list.search')}
        filters={filters}
        defaultSort={{ columnId: 'name', dir: 'asc' }}
        onRowClick={(c) => (c.is_deleted ? undefined : navigate(`/challenges/${c.id}`))}
        toolbarActions={
          isOrgAdmin ? (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label={t('archive.showArchived')}
            />
          ) : undefined
        }
      />

      <ConfirmDialog
        open={!!toDelete}
        title={t('challenge.detail.delete')}
        message={
          deleteStationNames.length > 0
            ? t('challenge.detail.deleteInUseConfirm', {
                count: deleteStationNames.length,
                stations: deleteStationNames.join(', '),
              })
            : t('challenge.detail.deleteConfirm')
        }
        destructive
        loading={deleting}
        confirmLabel={t('actions.delete', { ns: 'common' })}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />

      <ConfirmDialog
        open={!!toDeleteForever}
        title={t('archive.deleteForeverTitle')}
        message={t('archive.deleteForeverConfirm')}
        destructive
        loading={deleting}
        confirmLabel={t('archive.deleteForever')}
        onConfirm={handleDeleteForever}
        onCancel={() => setToDeleteForever(null)}
      />
    </Box>
  );
};

export default ChallengeLibraryList;
